import { useState, useEffect } from "react"
import { Loader2, FileCheck, TrendingUp } from "lucide-react"
import CoachSidebar from "../components/CoachSidebar"
import { Link } from "../lib/router"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function CoachDashboard() {
  const [stats, setStats] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const { t } = useLanguage()

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  useEffect(() => {
    async function fetchStats() {
      setIsLoading(true)
      try {
        const res = await fetch(`${API}/api/coach/dashboard`, { headers: authHeaders() })
        if (!res.ok) throw new Error("Failed to fetch coach stats")
        setStats(await res.json())
      } catch (e) {
        setError(e.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchStats()
  }, [])

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <CoachSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10">
        <header className="mb-10 flex items-end justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("coach_dash_title")}</h1>
            <p className="mt-1 text-sm text-mist-300">{t("coach_dash_subtitle")}</p>
          </div>
        </header>

        {error && <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
        ) : stats ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            
            {/* Pending Actions Card */}
            <Link to="/coach/verification" className="block rounded-xl border border-gold-500/30 bg-ink-800/50 p-6 shadow-sm hover:bg-ink-800/80 transition-colors">
              <h2 className="mb-6 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gold-400">
                <FileCheck className="h-5 w-5" /> {t("coach_dash_pending_verif")}
              </h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-display font-bold text-paper-0">{stats.pending_members}</p>
                  <p className="text-sm font-medium text-mist-400 mt-1">{t("coach_dash_pending_members")}</p>
                </div>
                <div className="h-12 w-px bg-white/10" />
                <div className="text-right">
                  <p className="text-3xl font-display font-bold text-paper-0">{stats.pending_registrations}</p>
                  <p className="text-sm font-medium text-mist-400 mt-1">{t("coach_dash_pending_regs")}</p>
                </div>
              </div>
            </Link>

            {/* Approved Stats Card */}
            <div className="rounded-xl border border-white/10 bg-ink-800/30 p-6 shadow-sm">
              <h2 className="mb-6 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-mist-300">
                <TrendingUp className="h-5 w-5" /> {t("coach_dash_team_stats")}
              </h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-display font-bold text-paper-0">{stats.approved_members}</p>
                  <p className="text-sm font-medium text-mist-400 mt-1">{t("coach_dash_approved_members")}</p>
                </div>
                <div className="h-12 w-px bg-white/10" />
                <div className="text-right">
                  <p className="text-3xl font-display font-bold text-paper-0">{stats.approved_registrations}</p>
                  <p className="text-sm font-medium text-mist-400 mt-1">{t("coach_dash_approved_regs")}</p>
                </div>
              </div>
            </div>

          </div>
        ) : null}
      </main>
    </div>
  )
}
